import { BrowserName, DeviceType, PlatformName } from "./enumnames";
import { PlatformDetails } from "./platform";

// Rename to CommonDeviceType to avoid duplicate identifier from PlatformAPI.DeviceType
export const enum PayloadDeviceType {
    DESKTOP = "Desktop",
    LAPTOP = "Laptop",
    TV = "TV",
    PHONE = "Phone",
    TABLET = "Tablet",
    SERVER = "Server",
    CONSOLE = "Console",
    UNDEFINED = "undefined"
}

export const enum PayloadBrowserType {
    CHROME = "Chrome",
    SAFARI = "Safari",
    YANDEX = "Yandex",
    EDGE = "Edge",
    EDGE_LEGACY = "Edge_legacy",
    FIREFOX = "Firefox",
    SAMSUNG = "Samsung",
    CHROMIUM = "Chromium",
    OPERA = "Opera",
    BRAVE = "Brave",
    SILK = "Silk",
    UNDEFINED = "undefined"
}

export const enum PayloadOSType {
    WINDOWS = "Windows",
    MACOS = "MacOS",
    SHIELD = "Shield",
    ANDROID = "Android",
    IOS = "iOS",
    IPADOS = "iPadOS",
    CHROMEOS = "ChromeOS",
    LINUX = "Linux",
    TIZEN = "Tizen",
    WEBOS = "WebOS",
    TVOS = "tvOS",
    XBOX = "Xbox",
    STEAMOS = "SteamOS",
    UNDEFINED = "undefined"
}

export const enum ClientHeaderOSType {
    WINDOWS = "WINDOWS",
    MACOS = "MACOS",
    SHIELD = "SHIELD",
    ANDROID = "ANDROID",
    IOS = "IOS",
    IPADOS = "IPADOS",
    CHROMEOS = "CHROMEOS",
    LINUX = "LINUX",
    TIZEN = "TIZEN",
    WEBOS = "WEBOS",
    XBOX = "XBOX",
    STEAMOS = "STEAMOS",
    UNKNOWN = "UNKNOWN"
}

export const enum ClientHeaderBrowserType {
    CHROME = "CHROME",
    SAFARI = "SAFARI",
    YANDEX = "YANDEX",
    EDGE = "EDGE",
    EDGE_LEGACY = "EDGE_LEGACY",
    FIREFOX = "FIREFOX",
    SAMSUNG = "SAMSUNG",
    CHROMIUM = "CHROMIUM",
    OPERA = "OPERA",
    BRAVE = "BRAVE",
    SILK = "SILK",
    UNKNOWN = "UNKNOWN"
}

export const enum ClientHeaderDeviceType {
    DESKTOP = "DESKTOP",
    LAPTOP = "LAPTOP",
    TV = "TV",
    PHONE = "PHONE",
    TABLET = "TABLET",
    CONSOLE = "CONSOLE",
    UNKNOWN = "UNKNOWN"
}

export declare interface PayloadPlatformDetails {
    // Name of the detected OS/Platform
    readonly os: PayloadOSType;
    // Name of the detected browser
    readonly browser: PayloadBrowserType;
    // deviceType in the format Nvidia backend services expects
    readonly deviceType: PayloadDeviceType;
}

export declare interface ClientHeaderPlatformDetails {
    // Name of the detected OS/Platform
    readonly os: ClientHeaderOSType;
    // Name of the detected browser
    readonly browser: ClientHeaderBrowserType;
    // deviceType in the format Nvidia backend services expects
    readonly deviceType?: ClientHeaderDeviceType;
}

class TranslatedPlatformDetails {
    // -----------------------------
    // GXT / telemetry style values
    // -----------------------------

    private static getMappedDeviceType(platformDeviceType?: DeviceType): PayloadDeviceType {
        // Mapping the PlatformDetails.DeviceType to GXT common fields.
        // If no mapped value, should return Undefined string.
        switch (platformDeviceType) {
            case DeviceType.CONSOLE:
                return PayloadDeviceType.CONSOLE;
            case DeviceType.TABLET:
                return PayloadDeviceType.TABLET;
            case DeviceType.PHONE:
                return PayloadDeviceType.PHONE;
            case DeviceType.TV:
                return PayloadDeviceType.TV;
            case DeviceType.LAPTOP:
                return PayloadDeviceType.LAPTOP;
            case DeviceType.DESKTOP:
                return PayloadDeviceType.DESKTOP;
            default:
                return PayloadDeviceType.UNDEFINED;
        }
    }

    private static getMappedBrowserName(browserName: BrowserName): PayloadBrowserType {
        switch (browserName) {
            case BrowserName.CHROME:
                return PayloadBrowserType.CHROME;
            case BrowserName.SAFARI:
                return PayloadBrowserType.SAFARI;
            case BrowserName.YANDEX:
                return PayloadBrowserType.YANDEX;
            case BrowserName.EDGE:
                return PayloadBrowserType.EDGE;
            case BrowserName.EDGE_LEGACY:
                return PayloadBrowserType.EDGE_LEGACY;
            case BrowserName.FIREFOX:
                return PayloadBrowserType.FIREFOX;
            case BrowserName.SAMSUNG:
                return PayloadBrowserType.SAMSUNG;
            case BrowserName.CHROMIUM:
                return PayloadBrowserType.CHROMIUM;
            case BrowserName.OPERA:
                return PayloadBrowserType.OPERA;
            case BrowserName.BRAVE:
                return PayloadBrowserType.BRAVE;
            case BrowserName.SILK:
                return PayloadBrowserType.SILK;
            case BrowserName.UNKNOWN:
            // As per comment https://nvbugswb.nvidia.com/NVBugs5/redir.aspx?url=/3434141/6 ReactNative browser
            // is not supported and is mapped to undefined.
            case BrowserName.REACT:
            default:
                return PayloadBrowserType.UNDEFINED;
        }
    }

    private static getMappedDeviceOS(platformOS: PlatformName): PayloadOSType {
        switch (platformOS) {
            case PlatformName.WINDOWS:
                return PayloadOSType.WINDOWS;
            case PlatformName.MAC:
                return PayloadOSType.MACOS;
            case PlatformName.CHROME_OS:
                return PayloadOSType.CHROMEOS;
            case PlatformName.LINUX:
                return PayloadOSType.LINUX;
            case PlatformName.IOS:
                return PayloadOSType.IOS;
            case PlatformName.IPADOS:
                return PayloadOSType.IPADOS;
            case PlatformName.TIZEN:
                return PayloadOSType.TIZEN;
            case PlatformName.WEBOS:
                return PayloadOSType.WEBOS;
            case PlatformName.XBOX:
                return PayloadOSType.XBOX;
            case PlatformName.ANDROID:
                return PayloadOSType.ANDROID;
            case PlatformName.STEAMOS:
                return PayloadOSType.STEAMOS;
            case PlatformName.FIRETV:
            case PlatformName.FREEBSD:
            case PlatformName.CCOS:
            case PlatformName.UNKNOWN:
            default:
                return PayloadOSType.UNDEFINED;
        }
    }

    // --------------------
    // LCARS style values
    // --------------------

    private static mapOSPlatformNameToSupportedDeviceOs = (
        osPlatformName: PayloadOSType
    ): ClientHeaderOSType => {
        switch (osPlatformName) {
            case PayloadOSType.WINDOWS:
                return ClientHeaderOSType.WINDOWS;
            case PayloadOSType.MACOS:
                return ClientHeaderOSType.MACOS;
            case PayloadOSType.LINUX:
                return ClientHeaderOSType.LINUX;
            case PayloadOSType.CHROMEOS:
                return ClientHeaderOSType.CHROMEOS;
            case PayloadOSType.IOS:
                return ClientHeaderOSType.IOS;
            case PayloadOSType.IPADOS:
                return ClientHeaderOSType.IPADOS;
            case PayloadOSType.WEBOS:
                return ClientHeaderOSType.WEBOS;
            case PayloadOSType.TIZEN:
                return ClientHeaderOSType.TIZEN;
            case PayloadOSType.XBOX:
                return ClientHeaderOSType.XBOX;
            case PayloadOSType.ANDROID:
                return ClientHeaderOSType.ANDROID;
            case PayloadOSType.STEAMOS:
                return ClientHeaderOSType.STEAMOS;
            case PayloadOSType.SHIELD:
                return ClientHeaderOSType.SHIELD;
            default:
                return ClientHeaderOSType.UNKNOWN;
        }
    };

    private static extractDeviceTypeForBrowserClient = (
        deviceTypeFromPlatformAPI: string
    ): ClientHeaderDeviceType => {
        switch (deviceTypeFromPlatformAPI) {
            case PayloadDeviceType.DESKTOP:
                return ClientHeaderDeviceType.DESKTOP;
            case PayloadDeviceType.LAPTOP:
                return ClientHeaderDeviceType.LAPTOP;
            case PayloadDeviceType.TV:
                return ClientHeaderDeviceType.TV;
            case PayloadDeviceType.PHONE:
                return ClientHeaderDeviceType.PHONE;
            case PayloadDeviceType.TABLET:
                return ClientHeaderDeviceType.TABLET;
            case PayloadDeviceType.CONSOLE:
                return ClientHeaderDeviceType.CONSOLE;
            default:
                return ClientHeaderDeviceType.DESKTOP;
        }
    };

    /*
      Common Headers expect UNKNOWN in place of UNDEFINED. 'Undefined' is required by Telemetry and
      is being used all over the codebase, so making the endpoint specific changes here.
      Refer to: https://confluence.nvidia.com/display/DCPC/LCARS+Documentation
    */
    private static extractBrowserTypeForBrowserClient = (
        browserName: PayloadBrowserType
    ): ClientHeaderBrowserType => {
        switch (browserName) {
            case PayloadBrowserType.CHROME:
                return ClientHeaderBrowserType.CHROME;
            case PayloadBrowserType.SAFARI:
                return ClientHeaderBrowserType.SAFARI;
            case PayloadBrowserType.YANDEX:
                return ClientHeaderBrowserType.YANDEX;
            case PayloadBrowserType.EDGE:
                return ClientHeaderBrowserType.EDGE;
            case PayloadBrowserType.EDGE_LEGACY:
                return ClientHeaderBrowserType.EDGE_LEGACY;
            case PayloadBrowserType.FIREFOX:
                return ClientHeaderBrowserType.FIREFOX;
            case PayloadBrowserType.SAMSUNG:
                return ClientHeaderBrowserType.SAMSUNG;
            case PayloadBrowserType.CHROMIUM:
                return ClientHeaderBrowserType.CHROMIUM;
            case PayloadBrowserType.OPERA:
                return ClientHeaderBrowserType.OPERA;
            case PayloadBrowserType.BRAVE:
                return ClientHeaderBrowserType.BRAVE;
            case PayloadBrowserType.SILK:
                return ClientHeaderBrowserType.SILK;
            case PayloadBrowserType.UNDEFINED:
                return ClientHeaderBrowserType.UNKNOWN;
            default:
                return ClientHeaderBrowserType.UNKNOWN;
        }
    };

    private readonly _platformDetails: PlatformDetails;
    private readonly _gxtDetails: PayloadPlatformDetails;
    private readonly _lcarsDetails: ClientHeaderPlatformDetails;

    constructor(details: PlatformDetails) {
        this._platformDetails = details;
        this._gxtDetails = this.translateForPayload();
        this._lcarsDetails = this.translateForClientHeaders();
    }

    private translateForPayload(): PayloadPlatformDetails {
        const details = this._platformDetails;
        const devType: PayloadDeviceType = TranslatedPlatformDetails.getMappedDeviceType(
            details.deviceType
        );
        const browser: PayloadBrowserType = TranslatedPlatformDetails.getMappedBrowserName(
            details.browser
        );
        const os: PayloadOSType = TranslatedPlatformDetails.getMappedDeviceOS(details.os);

        return {
            os: os,
            browser: browser,
            deviceType: devType
        };
    }

    private translateForClientHeaders(): ClientHeaderPlatformDetails {
        const details = this._platformDetails;
        // TODO: Make these use more direct lookups!
        const devType: ClientHeaderDeviceType =
            TranslatedPlatformDetails.extractDeviceTypeForBrowserClient(
                TranslatedPlatformDetails.getMappedDeviceType(details.deviceType)
            );
        const browser: ClientHeaderBrowserType =
            TranslatedPlatformDetails.extractBrowserTypeForBrowserClient(
                TranslatedPlatformDetails.getMappedBrowserName(details.browser)
            );
        const os: ClientHeaderOSType =
            TranslatedPlatformDetails.mapOSPlatformNameToSupportedDeviceOs(
                TranslatedPlatformDetails.getMappedDeviceOS(details.os)
            );

        return {
            os: os,
            browser: browser,
            deviceType: devType
        };
    }

    public get gxtDetails() {
        return this._gxtDetails;
    }

    public get lcarsDetails() {
        return this._lcarsDetails;
    }

    public get details() {
        return this._platformDetails;
    }
}

let lastTranslatedDetails: TranslatedPlatformDetails | undefined;

export function translatePlatformDetailsForPayload(
    details: PlatformDetails
): PayloadPlatformDetails {
    if (lastTranslatedDetails?.details != details) {
        lastTranslatedDetails = new TranslatedPlatformDetails(details);
    }

    return lastTranslatedDetails!.gxtDetails;
}

export function translatePlatformDetailsForClientHeader(
    details: PlatformDetails
): ClientHeaderPlatformDetails {
    if (lastTranslatedDetails?.details != details) {
        lastTranslatedDetails = new TranslatedPlatformDetails(details);
    }

    return lastTranslatedDetails!.lcarsDetails;
}
